/// <reference types="user-agent-data-types" />

import { useEffect, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import { useMediaQuery, useSessionStorage } from 'usehooks-ts';
import { z } from 'zod';
import { BUILD_ENV, DISPLAY_STORAGE_KEY } from '@/utils/constants';
import { getStandaloneMode } from '@/utils/get-standalone-mode';

export type StandaloneMode = 'standalone' | 'embedded' | 'web';

export interface ClientHints {
  osType: DisplayMode;
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
}

interface UserHints {
  browser: string;
  hints: ClientHints;
}

interface UserPlatform {
  mode: StandaloneMode;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isStandalone: boolean;
  isEmbedded: boolean;
  supportsTouch: boolean;
}

export default function useUserPlatform(): UserPlatform {
  const isDesktop = useIsDesktop();

  const isStandalone = getStandaloneMode();
  const isEmbedded = BUILD_ENV === 'embedded';

  const hasTouchPoints = navigator.maxTouchPoints > 0;
  const hasTouchEvent = 'ontouchstart' in window;
  const isCoarse = window.matchMedia('(pointer: coarse)').matches;

  return {
    mode: isEmbedded ? 'embedded' : isStandalone ? 'standalone' : 'web',
    isStandalone,
    isEmbedded,
    isDesktop,
    supportsTouch: hasTouchPoints || hasTouchEvent || isCoarse,
    ...getUserPlatform(),
  };
}

export function useUserHints() {
  const [state, setState] = useState<UserHints>(() => ({
    browser: 'Unknown',
    hints: {
      osType: 'desktop',
      osName: '',
      osVersion: '',
      browserName: '',
      browserVersion: '',
    },
  }));

  useEffect(() => {
    (async () => {
      const hints = await getBrowserMetadata();
      setState((s) => ({
        ...s,
        browser: [hints.osName, hints.osVersion, hints.browserName, hints.browserVersion].join(' '),
        hints,
      }));
    })();
  }, []);

  return state;
}

export async function getUserAgent(): Promise<UserHints> {
  const hints = await getBrowserMetadata();

  return {
    browser: [hints.osName, hints.osVersion, hints.browserName, hints.browserVersion].join(' '),
    hints,
  };
}

export function getUserPlatform(): Readonly<Pick<UserPlatform, 'isIOS' | 'isAndroid'>> {
  const ua = UAParser(navigator.userAgent);

  return {
    isIOS: ua?.os.name === 'iOS' || (ua?.os.name === 'Mac OS' && navigator.maxTouchPoints >= 1),
    isAndroid: ua?.os.name === 'Android',
  };
}

export async function getBrowserMetadata(): Promise<ClientHints> {
  const result: ClientHints = {
    osType: 'desktop',
    osName: '',
    osVersion: '',
    browserName: '',
    browserVersion: '',
  };

  const ua = UAParser(navigator.userAgent);
  if (!ua) return result;

  const os = await ua.os.withClientHints();
  const browser = await ua.browser.withClientHints();

  result.osType = getOsTypeByName(os.name);
  result.osName = os.name ?? '';
  result.osVersion = os.version ?? '';
  result.browserName = browser.name ?? '';
  result.browserVersion = browser.version ?? '';

  return result;
}

function getOsTypeByName(osName: string | undefined): DisplayMode {
  switch (osName) {
    case 'iOS':
    case 'Android':
      return 'mobile';
    default:
      return 'desktop';
  }
}

export function useIsDesktop() {
  const [displayMode] = useDisplayMode();

  return displayMode === 'desktop';
}

export const useScreenOrientation = () => (useMediaQuery('(orientation: portrait)') ? 'portrait' : 'landscape');

const displayModeSchema = z.enum(['desktop', 'mobile']);

export type DisplayMode = z.infer<typeof displayModeSchema>;

export function useDisplayMode() {
  return useSessionStorage(DISPLAY_STORAGE_KEY, () => getDisplayMode());
}

export function getDisplayMode(): DisplayMode {
  const storedValue = sessionStorage.getItem(DISPLAY_STORAGE_KEY);

  if (BUILD_ENV === 'embedded') {
    sessionStorage.setItem(DISPLAY_STORAGE_KEY, JSON.stringify('mobile'));
    return 'mobile';
  }

  if (storedValue === null) {
    const ua = UAParser(navigator.userAgent);
    if (!ua) {
      return 'mobile';
    }
    const value = getOsTypeByName(ua.os.name);
    sessionStorage.setItem(DISPLAY_STORAGE_KEY, JSON.stringify(value));
    return value;
  }

  try {
    const { success, data } = displayModeSchema.safeParse(JSON.parse(storedValue));

    if (!success) {
      sessionStorage.removeItem(DISPLAY_STORAGE_KEY);
      return getDisplayMode();
    }

    return data;
  } catch {
    sessionStorage.removeItem(DISPLAY_STORAGE_KEY);
    return getDisplayMode();
  }
}

export const useShouldRetractRightSection = () => useMediaQuery('(max-width: 900px)');
